import { zodResolver } from '@hookform/resolvers/zod';
import {
  CircleCheckIcon,
  HashIcon,
  MapPinIcon,
  RefreshCwIcon,
  SaveIcon,
  StoreIcon,
} from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import z from 'zod';

import { WarehouseTenantDTO } from '@invenco/warehousing-interface';

import { Button } from '@/components/core/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { DetailGroup } from '@/components/detail-group';
import { DetailList } from '@/components/detail-list';
import { TagsFormField } from '@/components/forms/tags-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';

import { tenantModeType } from '../../../../shared/status-maps';
import { useTenant } from './useTenant';

const TENANT_OPTIONS_FORM_ID = 'tenant-options-form';

const tenantOptionsSchema = z.object({
  workStreamName: z.string(),
  packMethod: z.string(),
  tariffCode: z.string(),
  allowablePickStrategies: z.array(z.string()),
});

type TenantOptionsFormValues = z.infer<typeof tenantOptionsSchema>;

function getTenantOptionsFormValues(
  warehouseTenantOptions: Partial<
    NonNullable<WarehouseTenantDTO['warehouseTenantOptions']>
  > | null = {},
): TenantOptionsFormValues {
  return {
    workStreamName: warehouseTenantOptions?.workStreamName ?? '',
    packMethod: warehouseTenantOptions?.packMethod ?? '',
    tariffCode: warehouseTenantOptions?.tariffCode ?? '',
    allowablePickStrategies: warehouseTenantOptions?.allowablePickStrategies ?? [],
  };
}

export function TenantDetails() {
  const { id } = useParams<{ id: string }>();
  const {
    warehouseTenant,
    isLoading,
    refetchTenant,
    updateWarehouseTenant,
    isUpdatingWarehouseTenant,
  } = useTenant(id);
  const form = useForm<TenantOptionsFormValues>({
    resolver: zodResolver(tenantOptionsSchema),
    defaultValues: getTenantOptionsFormValues(),
  });

  useEffect(() => {
    form.reset(getTenantOptionsFormValues(warehouseTenant?.warehouseTenantOptions));
  }, [warehouseTenant, form]);

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/tenants" parentTitle="Tenants">
          <StoreIcon />
        </HeaderIcon>
        <HeaderTitle>{warehouseTenant?.tenant?.name}</HeaderTitle>
        <HeaderActions>
          <Button
            type="submit"
            form={TENANT_OPTIONS_FORM_ID}
            loading={isUpdatingWarehouseTenant}
            disabled={isLoading}
          >
            <SaveIcon />
            Save
          </Button>
          <Button
            size="icon"
            variant="outline"
            aria-label="Refresh"
            onClick={() => void refetchTenant()}
            disabled={isLoading}
          >
            <RefreshCwIcon />
          </Button>
        </HeaderActions>
      </Header>
      <PageContent variant="split">
        <DetailMain>
          <Card>
            <CardHeader>
              <CardTitle>Options</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  id={TENANT_OPTIONS_FORM_ID}
                  className="grid gap-4"
                  onSubmit={form.handleSubmit((values) => void updateWarehouseTenant(values))}
                >
                  <TextFormField
                    control={form.control}
                    name="workStreamName"
                    label="Work stream name"
                    disabled={isLoading}
                  />
                  <TextFormField
                    control={form.control}
                    name="packMethod"
                    label="Pack method"
                    disabled={isLoading}
                  />
                  <TextFormField
                    control={form.control}
                    name="tariffCode"
                    label="Tariff code"
                    disabled={isLoading}
                  />
                  <TagsFormField
                    control={form.control}
                    name="allowablePickStrategies"
                    label="Allowable pick strategies"
                    disabled={isLoading}
                    caseSensitive
                  />
                </form>
              </Form>
            </CardContent>
          </Card>
        </DetailMain>
        <DetailSide>
          <DetailGroup>
            <DetailList
              title="Details"
              isLoading={isLoading}
              items={[
                {
                  icon: MapPinIcon,
                  label: 'Location',
                  value: warehouseTenant?.warehouse?.name,
                },
                { icon: HashIcon, label: 'Code', value: warehouseTenant?.tenant?.code },
                {
                  icon: CircleCheckIcon,
                  label: 'Mode',
                  value: (
                    <StatusBadge status={warehouseTenant?.tenant?.mode} type={tenantModeType} />
                  ),
                },
              ]}
            />
          </DetailGroup>
        </DetailSide>
      </PageContent>
    </Page>
  );
}
